import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, Download, Copy, Clapperboard, Film } from "lucide-react";
import { useT } from "@/i18n";
import { useVideoJob } from "@/context/VideoJobContext";
import { useAuth } from "@/context/AuthContext";
import VideoUpgradeGate from "@/components/VideoUpgradeGate";

const VOICES = ["nova", "alloy", "echo", "fable", "onyx", "shimmer", "sage", "coral", "ash"];
const LANGUAGES = [
  { key: "en", label: "English" },
  { key: "tr", label: "Türkçe" },
  { key: "es", label: "Español" },
  { key: "fr", label: "Français" },
  { key: "de", label: "Deutsch" },
  { key: "it", label: "Italiano" },
  { key: "pt", label: "Português" },
  { key: "ja", label: "日本語" },
  { key: "zh", label: "中文" },
];

const STAGE_KEY_FOR = (t) => ({
  queued: t("stage_queued"),
  script: t("stage_script"),
  images_audio: t("stage_images_audio"),
  stitching: t("stage_stitching"),
  done: t("stage_done"),
});

export default function VideoProducer() {
  const { t } = useT();
  const { user } = useAuth();
  const { activeJob, result: ctxResult, startJob, onDone, clearResult } = useVideoJob();
  // Free-tier (and expired trials rolled back to free) cannot access video.
  // Admin always passes through.
  const planId = user?.plan || "free";
  const hasVideoAccess = !!user?.is_admin || planId === "creator" || planId === "pro";
  const TONES = [
    { key: "informative", label: "informative" },
    { key: "casual", label: "casual" },
    { key: "dramatic", label: "dramatic" },
    { key: "educational", label: "educational" },
  ];
  const LENGTHS = [
    { key: "short", label: "short · ~32s · 4 scenes" },
    { key: "medium", label: "medium · ~48s · 6 scenes" },
    { key: "long", label: "long · ~64s · 8 scenes" },
    { key: "xl", label: "xl · ~80s · 10 scenes" },
    { key: "xxl", label: "xxl · ~112s · 14 scenes" },
  ];

  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("informative");
  const [length, setLength] = useState("short");
  const [voice, setVoice] = useState("nova");
  const [language, setLanguage] = useState("en");
  const [result, setResult] = useState(null);

  // Loading state is derived from activeJob in context
  const loading = !!activeJob;
  const stage = activeJob?.stage || activeJob?.status || "";

  // Pre-fill from active job if user navigates back
  useEffect(() => {
    if (activeJob && !topic) {
      setTopic(activeJob.topic || "");
      setTone(activeJob.tone || "informative");
      setLength(activeJob.length || "short");
      setVoice(activeJob.voice || "nova");
      setLanguage(activeJob.language || "en");
    }
  }, [activeJob, topic]);

  // Reflect result delivered while we were away
  useEffect(() => {
    if (ctxResult) setResult(ctxResult);
  }, [ctxResult]);

  // Also catch finished events while on the page
  useEffect(() => {
    return onDone(({ ok, result: r }) => {
      if (ok && r) setResult(r);
    });
  }, [onDone]);

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      toast.error(t("err_topic_required"));
      return;
    }
    if (loading) {
      toast.error(t("video_already_running") || "A video is already being generated.");
      return;
    }
    setResult(null);
    clearResult();
    try {
      await startJob({ topic, tone, length, voice, language });
    } catch (err) {
      if (err.message !== "job-already-active") {
        toast.error(err?.response?.data?.detail || t("err_generic"));
      }
    }
  };

  const downloadImage = (b64, idx) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${b64}`;
    link.download = `lumioo-scene-${idx + 1}.png`;
    link.click();
  };

  const downloadAudio = () => {
    if (!result?.voiceover_base64) return;
    const link = document.createElement("a");
    link.href = `data:audio/mp3;base64,${result.voiceover_base64}`;
    link.download = "lumioo-voiceover.mp3";
    link.click();
  };

  const downloadVideo = () => {
    if (!result?.video_url) return;
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_BACKEND_URL}${result.video_url}`;
    link.download = "lumioo-video.mp4";
    link.target = "_blank";
    link.click();
  };

  const copyMeta = () => {
    const text = `Title: ${result.title}\n\nDescription:\n${result.description}\n\nHashtags: ${(result.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(text);
    toast.success(t("video_meta_copied"));
  };

  return (
    <div data-testid="video-producer-page">
      {!hasVideoAccess && <VideoUpgradeGate />}
      {hasVideoAccess && (<>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("video_eyebrow")}</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("video_h1")}</h1>
      <p className="mt-2 text-zinc-600">{t("video_sub")}</p>

      <form onSubmit={submit} className="mt-10 border border-zinc-200 bg-white p-6 sm:p-8 grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="video-form">
        <div className="md:col-span-12">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("video_topic")}</label>
          <input
            data-testid="video-topic-input"
            type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder={t("video_topic_ph")}
            className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 rounded-md"
          />
        </div>

        <div className="md:col-span-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("video_tone")}</label>
          <select data-testid="video-tone-select" value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:border-blue-600">
            {TONES.map((tn) => <option key={tn.key} value={tn.key}>{tn.label}</option>)}
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("video_length")}</label>
          <select data-testid="video-length-select" value={length} onChange={(e) => setLength(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:border-blue-600">
            {LENGTHS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("video_voice")}</label>
          <select data-testid="video-voice-select" value={voice} onChange={(e) => setVoice(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:border-blue-600">
            {VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("video_language")}</label>
          <select data-testid="video-language-select" value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-zinc-200 bg-white rounded-md text-sm focus:outline-none focus:border-blue-600">
            {LANGUAGES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
        </div>

        <div className="md:col-span-12">
          <button type="submit" data-testid="video-submit-btn" disabled={loading} className="btn-accent w-full inline-flex items-center justify-center gap-2 font-semibold py-3.5 rounded-md disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {loading ? t("video_generating") : t("video_generate")}
          </button>
        </div>
      </form>

      {!result && !loading && (
        <div className="mt-10 border border-dashed border-zinc-300 bg-white p-12 text-center" data-testid="video-empty-state">
          <Clapperboard className="w-10 h-10 mx-auto text-zinc-400" strokeWidth={1.2} />
          <p className="mt-3 text-sm text-zinc-500">{t("video_empty")}</p>
        </div>
      )}

      {loading && (
        <div className="mt-10 border border-zinc-200 bg-white p-12 text-center" data-testid="video-loading">
          <Loader2 className="w-7 h-7 mx-auto animate-spin text-zinc-600" />
          <p className="mt-3 text-sm text-zinc-700 font-semibold">{STAGE_KEY_FOR(t)[stage] || t("video_generating")}</p>
          <p className="mt-1 text-xs text-zinc-500">{t("video_loading_sub")}</p>
        </div>
      )}

      {result && (
        <div className="mt-10 space-y-6" data-testid="video-result">
          {/* MP4 Player + Download */}
          {result.video_url ? (
            <div className="border border-zinc-200 bg-white p-6 sm:p-8" data-testid="video-mp4-block">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_h1")}</div>
                  <div className="text-base font-semibold mt-0.5 flex items-center gap-2">
                    <Film className="w-4 h-4 text-blue-600" /> Sora 2 · 1280×720
                  </div>
                </div>
                <button onClick={downloadVideo} data-testid="video-download-mp4" className="btn-accent inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md">
                  <Download className="w-4 h-4" /> {t("video_download_video")}
                </button>
              </div>
              <video
                controls
                src={`${process.env.REACT_APP_BACKEND_URL}${result.video_url}`}
                className="w-full mt-4 rounded-md border border-zinc-200 bg-black"
                data-testid="video-mp4-player"
              />
            </div>
          ) : (
            <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {t("video_no_mp4")}
            </div>
          )}

          {/* Meta */}
          <div className="border border-zinc-200 bg-white p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_title_l")}</div>
                <h2 className="mt-1 text-2xl font-bold tracking-tight" data-testid="video-title">{result.title}</h2>
              </div>
              <button onClick={copyMeta} data-testid="video-copy-meta" className="btn-outline inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md">
                <Copy className="w-4 h-4" /> {t("video_copy_meta")}
              </button>
            </div>
            <div className="mt-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_desc_l")}</div>
              <p className="mt-1 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="video-description">{result.description}</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {(result.hashtags || []).map((h, i) => (
                <span key={i} className="px-2.5 py-1 text-xs font-mono bg-zinc-100 border border-zinc-200 rounded text-zinc-700">{h}</span>
              ))}
            </div>
          </div>

          {/* Voiceover */}
          {result.voiceover_base64 && (
            <div className="border border-zinc-200 bg-white p-6 sm:p-8" data-testid="video-voiceover">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("video_voiceover")}</div>
                  <div className="text-base font-semibold mt-0.5">{voice}</div>
                </div>
                <button onClick={downloadAudio} data-testid="video-download-audio" className="btn-primary inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md">
                  <Download className="w-4 h-4" /> {t("video_download_audio")}
                </button>
              </div>
              <audio controls src={`data:audio/mp3;base64,${result.voiceover_base64}`} className="w-full mt-4" />
            </div>
          )}

          {/* Scenes */}
          <div>
            <h3 className="text-lg font-bold tracking-tight">{t("video_scenes")}</h3>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.scenes.map((s, i) => (
                <div key={i} className="border border-zinc-200 bg-white overflow-hidden" data-testid={`scene-${i}`}>
                  <div className="aspect-video bg-zinc-100 relative">
                    {s.image_base64 ? (
                      <img src={`data:image/png;base64,${s.image_base64}`} alt={`${t("scene")} ${i + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-xs">{t("image_unavailable")}</div>
                    )}
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-zinc-950 text-white text-[10px] font-mono uppercase tracking-wider">
                      {t("scene")} {i + 1}
                    </div>
                    {s.image_base64 && (
                      <button onClick={() => downloadImage(s.image_base64, i)} data-testid={`scene-download-${i}`} className="absolute bottom-2 right-2 px-2 py-1 bg-white/90 text-zinc-950 text-xs font-semibold rounded inline-flex items-center gap-1 hover:bg-white">
                        <Download className="w-3 h-3" /> PNG
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Narration</div>
                    <p className="mt-1 text-sm text-zinc-800 leading-relaxed">{s.narration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
